import React, { useState, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Typography, CircularProgress, Tooltip, IconButton, TextField,
  MenuItem, Grid, Alert,
} from '@mui/material';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import MedicationIcon from '@mui/icons-material/Medication';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { PageIndices, TabIndices } from '../../common/i-doc/i-doc.registry.ts';
import { DashboardLayout } from '@components/layout/DashboardLayout.tsx';
import { AdaptiveTabs } from '@components/common/AdaptiveTabs.tsx';
import { PageCard } from '@components/common/PageCard.tsx';
import { PageToolbar } from '@components/common/PageToolbar.tsx';
import { UniversalPagination } from '@components/common/UniversalPagination.tsx';
import { PageHint } from '@components/common/PageHint.tsx';
import { ConfirmationDialog } from '@components/common/ConfirmationDialog.tsx';
import { toastService } from '@services/toast.service.ts';
import {
  adminAiCacheService,
  AiCacheListParams,
  AiCacheListResponse,
} from '@services/admin-ai-cache.service.ts';
import { useDebounce } from '@hooks/useDebounce.ts';

// ---------- Types ----------

interface ExtractCacheItem {
  id: number;
  textKey: string;
  originalText: string;
  locale: string;
  suggestedTitle: string;
  items: any[];
  source: 'ai' | 'manual';
  hitCount: number;
  createdAt: string;
  updatedAt: string;
}

interface TabConfig {
  hint: string;
  columnLabel: string;
  countLabel: string;
  itemsLabel: string;
  storageKey: string;
  tabIndex: string;
  fetchFn: (params: AiCacheListParams) => Promise<AiCacheListResponse<ExtractCacheItem>>;
  updateFn: (id: number, data: any) => Promise<ExtractCacheItem>;
  deleteFn: (id: number) => Promise<void>;
  formatItems: (items: any[]) => string;
  renderDetailItems: (items: any[]) => React.ReactNode;
}

// ---------- Tab configs ----------

const WORKOUT_INTENSITY_LABELS: Record<string, string> = { low: 'Низька', medium: 'Середня', high: 'Висока' };

const TABS_CONFIG: Record<string, TabConfig> = {
  food: {
    hint: 'Журнал розпізнавання їжі. Кожен унікальний текст зберігається після першого розпізнавання через Gemini. Повторний ідентичний запит повертається з кешу без виклику AI.',
    columnLabel: 'Розпізнані продукти',
    countLabel: 'Продуктів',
    itemsLabel: 'Продукти',
    storageKey: 'food',
    tabIndex: TabIndices.ADMIN_AI_EXTRACT_FOOD,
    fetchFn: (p) => adminAiCacheService.getFoodExtractCache(p) as any,
    updateFn: (id, data) => adminAiCacheService.updateFoodExtractCache(id, data) as any,
    deleteFn: (id) => adminAiCacheService.deleteFoodExtractCache(id),
    formatItems: (items) => items.map((i: any) => `${i.name} ${i.totalAmount}${i.unit}`).join(', '),
    renderDetailItems: (items) => (
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Назва</TableCell>
            <TableCell>Тип</TableCell>
            <TableCell>Кількість</TableCell>
            <TableCell>Порція</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((it: any, idx: number) => (
            <TableRow key={idx}>
              <TableCell>{it.name}</TableCell>
              <TableCell>{it.type ?? '—'}</TableCell>
              <TableCell>{it.totalAmount} {it.unit}</TableCell>
              <TableCell>
                {it.portionName
                  ? `${it.portionName}${it.portionSize ? ` (${it.portionSize}${it.unit})` : ''}${it.portionCount ? ` × ${it.portionCount}` : ''}`
                  : '—'}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    ),
  },
  workout: {
    hint: 'Журнал розпізнавання тренувань. Кожен унікальний текст зберігається після першого розпізнавання через Gemini. Повторний ідентичний запит повертається з кешу без виклику AI.',
    columnLabel: 'Розпізнані вправи',
    countLabel: 'Вправ',
    itemsLabel: 'Вправи',
    storageKey: 'workout',
    tabIndex: TabIndices.ADMIN_AI_EXTRACT_WORKOUT,
    fetchFn: (p) => adminAiCacheService.getWorkoutExtractCache(p) as any,
    updateFn: (id, data) => adminAiCacheService.updateWorkoutExtractCache(id, data) as any,
    deleteFn: (id) => adminAiCacheService.deleteWorkoutExtractCache(id),
    // Розпізнавання тренувань повертає totalAmount (не quantity, як препарати)
    formatItems: (items) => items.map((i: any) =>
      `${i.name} ${i.totalAmount ?? '?'} ${i.unit ?? ''}${WORKOUT_INTENSITY_LABELS[i.intensity] ? ` · ${WORKOUT_INTENSITY_LABELS[i.intensity].toLowerCase()}` : ''}`,
    ).join(', '),
    renderDetailItems: (items) => (
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Назва</TableCell>
            <TableCell>Тип</TableCell>
            <TableCell>Кількість</TableCell>
            <TableCell>Інтенсивність</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((it: any, idx: number) => (
            <TableRow key={idx}>
              <TableCell>{it.name}</TableCell>
              <TableCell>{it.type ?? '—'}</TableCell>
              <TableCell>{it.totalAmount ?? '—'} {it.unit}</TableCell>
              <TableCell>{WORKOUT_INTENSITY_LABELS[it.intensity] ?? '—'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    ),
  },
  medication: {
    hint: 'Журнал розпізнавання медикаментів. Кожен унікальний текст зберігається після першого розпізнавання через Gemini. Повторний ідентичний запит повертається з кешу без виклику AI.',
    columnLabel: 'Розпізнані препарати',
    countLabel: 'Препаратів',
    itemsLabel: 'Препарати',
    storageKey: 'medication',
    tabIndex: TabIndices.ADMIN_AI_EXTRACT_MEDICATION,
    fetchFn: (p) => adminAiCacheService.getMedicationExtractCache(p) as any,
    updateFn: (id, data) => adminAiCacheService.updateMedicationExtractCache(id, data) as any,
    deleteFn: (id) => adminAiCacheService.deleteMedicationExtractCache(id),
    formatItems: (items) => items.map((i: any) => `${i.name} ${i.quantity} ${i.unit}`).join(', '),
    renderDetailItems: (items) => (
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Назва</TableCell>
            <TableCell>Кількість</TableCell>
            <TableCell>Одиниця</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((it: any, idx: number) => (
            <TableRow key={idx}>
              <TableCell>{it.name}</TableCell>
              <TableCell>{it.quantity}</TableCell>
              <TableCell>{it.unit}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    ),
  },
};

const SOURCE_COLOR: Record<string, 'default' | 'primary' | 'warning'> = {
  ai: 'primary',
  manual: 'warning',
};

// ---------- Detail modal ----------

function DetailModal({ item, open, onClose, config }: {
  item: ExtractCacheItem | null;
  open: boolean;
  onClose: () => void;
  config: TabConfig;
}) {
  if (!item) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Деталі запису #{item.id}</DialogTitle>
      <DialogContent dividers>
        <Table size="small" sx={{ mb: 2 }}>
          <TableBody>
            {[
              { label: 'ID', value: item.id },
              { label: 'Ключ (MD5)', value: item.textKey },
              { label: 'Локаль', value: item.locale },
              { label: 'Назва події', value: item.suggestedTitle },
              { label: 'Джерело', value: item.source === 'ai' ? 'AI' : 'Вручну' },
              { label: 'Звернень', value: item.hitCount },
              { label: 'Створено', value: new Date(item.createdAt).toLocaleString('uk') },
              { label: 'Оновлено', value: new Date(item.updatedAt).toLocaleString('uk') },
            ].map(({ label, value }) => (
              <TableRow key={label}>
                <TableCell sx={{ color: 'text.secondary', width: 160 }}>{label}</TableCell>
                <TableCell>{String(value)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <Typography variant="subtitle2" gutterBottom>Текст запиту</Typography>
        <Box sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 1, mb: 2 }}>
          <Typography variant="body2">{item.originalText}</Typography>
        </Box>

        <Typography variant="subtitle2" gutterBottom>
          {config.columnLabel} ({item.items.length})
        </Typography>
        {config.renderDetailItems(item.items)}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Закрити</Button>
      </DialogActions>
    </Dialog>
  );
}

// ---------- Edit modal ----------

function EditModal({ item, open, onClose, onSaved, config }: {
  item: ExtractCacheItem | null;
  open: boolean;
  onClose: () => void;
  onSaved: (updated: ExtractCacheItem) => void;
  config: TabConfig;
}) {
  const [suggestedTitle, setSuggestedTitle] = useState('');
  const [itemsJson, setItemsJson] = useState('');
  const [source, setSource] = useState<'ai' | 'manual'>('ai');
  const [jsonError, setJsonError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (item) {
      setSuggestedTitle(item.suggestedTitle);
      setItemsJson(JSON.stringify(item.items, null, 2));
      setSource(item.source);
      setJsonError('');
    }
  }, [item]);

  if (!item) return null;

  const handleItemsChange = (v: string) => {
    setItemsJson(v);
    try { JSON.parse(v); setJsonError(''); } catch { setJsonError('Невалідний JSON'); }
  };

  const handleSave = async () => {
    if (jsonError) return;
    let parsedItems: any[];
    try { parsedItems = JSON.parse(itemsJson); } catch { setJsonError('Невалідний JSON'); return; }

    setSaving(true);
    try {
      const updated = await config.updateFn(item.id, { suggestedTitle, items: parsedItems, source });
      toastService.success('Збережено');
      onSaved(updated);
      onClose();
    } catch (error: any) {
      toastService.error(error?.message || 'Не вдалося зберегти');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Редагування #{item.id}</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 1, mb: 2 }}>
          <Typography variant="caption" color="text.secondary">Оригінальний текст</Typography>
          <Typography variant="body2">{item.originalText}</Typography>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={8}>
            <TextField label="Назва події" fullWidth value={suggestedTitle} onChange={(e) => setSuggestedTitle(e.target.value)} />
          </Grid>
          <Grid item xs={4}>
            <TextField select label="Джерело" fullWidth value={source} onChange={(e) => setSource(e.target.value as 'ai' | 'manual')}>
              <MenuItem value="ai">AI</MenuItem>
              <MenuItem value="manual">Вручну</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
              {config.itemsLabel} (JSON-масив, поле scheduledAt не зберігається)
            </Typography>
            <TextField
              multiline rows={10} fullWidth
              value={itemsJson} onChange={(e) => handleItemsChange(e.target.value)}
              inputProps={{ style: { fontFamily: 'monospace', fontSize: 12 } }}
              error={!!jsonError}
            />
            {jsonError && <Alert severity="error" sx={{ mt: 1 }}>{jsonError}</Alert>}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Скасувати</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving || !!jsonError}>
          {saving ? 'Збереження…' : 'Зберегти'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ---------- Generic tab content ----------

function ExtractCacheTab({ config }: { config: TabConfig }) {
  const [items, setItems] = useState<ExtractCacheItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [search, setSearch] = useState('');
  const [source, setSource] = useState('');
  const [detail, setDetail] = useState<ExtractCacheItem | null>(null);
  const [editing, setEditing] = useState<ExtractCacheItem | null>(null);
  //const [deleting, setDeleting] = useState<ExtractCacheItem | null>(null);
  const debouncedSearch = useDebounce(search, 400);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await config.fetchFn({
        page, limit,
        search: debouncedSearch || undefined,
        source: source as any || undefined,
      });
      setItems(res.data);
      setTotal(res.meta.total);
    } catch {
      toastService.error('Не вдалося завантажити журнал.');
    } finally {
      setLoading(false);
    }
  }, [config, page, limit, debouncedSearch, source]);

  // Reset pagination and filters when tab changes
  useEffect(() => {
    setPage(1);
    setSearch('');
    setSource('');
  }, [config]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const handleReset = () => { setSearch(''); setSource(''); setPage(1); };

  const handleSaved = (updated: ExtractCacheItem) => {
    setItems((prev) => prev.map((i) => (i.id === updated.id ? updated : i)));
  };

  const handleDelete = () => {
    if (!deleting) return;
    const id = deleting.id;
    setDeleting(null);
    config.deleteFn(id).then(() => {
      setItems((prev) => prev.filter((i) => i.id !== id));
      setTotal((t) => t - 1);
      toastService.success('Запис видалено');
    }).catch(() => toastService.error('Не вдалося видалити'));
  };

  const [deleting, setDeleting] = useState<ExtractCacheItem | null>(null);

  return (
    <Box data-i-doc={config.tabIndex}>
      <PageCard>
        <PageToolbar
          storageKey={`filters:admin-ai-extract-cache-${config.storageKey}`}
          filters={[
            {
              id: 'search', type: 'text', label: 'Пошук за текстом запиту',
              value: search, onChange: (v) => { setSearch(v); setPage(1); }, flexGrow: 1,
            },
            {
              id: 'source', type: 'select', label: 'Джерело',
              value: source, onChange: (v) => { setSource(v); setPage(1); },
              options: [{ value: '', label: 'Всі' }, { value: 'ai', label: 'AI' }, { value: 'manual', label: 'Вручну' }],
            },
          ]}
          onReset={handleReset}
          showReset
        />

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
        ) : items.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>Записів не знайдено</Typography>
        ) : (
          <TableContainer>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Текст запиту</TableCell>
                  <TableCell>{config.columnLabel}</TableCell>
                  <TableCell align="center">{config.countLabel}</TableCell>
                  <TableCell align="center">Звернень</TableCell>
                  <TableCell align="center">Джерело</TableCell>
                  <TableCell>Оновлено</TableCell>
                  <TableCell align="center" />
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item) => (
                  <TableRow key={item.id} hover sx={{ cursor: 'pointer' }} onClick={() => setDetail(item)}>
                    <TableCell sx={{ maxWidth: 260 }}>
                      <Typography variant="body2" fontWeight={500} noWrap>{item.originalText}</Typography>
                      <Typography variant="caption" color="text.secondary">{item.suggestedTitle} · {item.locale}</Typography>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 300 }}>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {config.formatItems(item.items)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={item.items.length} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={item.hitCount} size="small" color={item.hitCount > 0 ? 'success' : 'default'} variant="outlined" />
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={item.source === 'ai' ? 'AI' : 'Вручну'} size="small" color={SOURCE_COLOR[item.source]} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(item.updatedAt).toLocaleString('uk')}
                      </Typography>
                    </TableCell>
                    <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                      <Tooltip title="Деталі">
                        <IconButton size="small" onClick={() => setDetail(item)}><InfoOutlinedIcon fontSize="small" /></IconButton>
                      </Tooltip>
                      <Tooltip title="Редагувати">
                        <IconButton size="small" onClick={() => setEditing(item)}><EditOutlinedIcon fontSize="small" /></IconButton>
                      </Tooltip>
                      <Tooltip title="Видалити (скине кеш)">
                        <IconButton size="small" color="error" onClick={() => setDeleting(item)}><DeleteOutlineIcon fontSize="small" /></IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <UniversalPagination
          page={page} count={Math.ceil(total / limit)} limit={limit} total={total}
          onPageChange={setPage} onLimitChange={(v) => { setLimit(v); setPage(1); }}
          storageKey={`pagination:admin-ai-extract-cache-${config.storageKey}`}
        />
      </PageCard>

      <DetailModal item={detail} open={!!detail} onClose={() => setDetail(null)} config={config} />
      <EditModal item={editing} open={!!editing} onClose={() => setEditing(null)} onSaved={handleSaved} config={config} />
      <ConfirmationDialog
        open={!!deleting}
        title="Видалити запис?"
        message={`Запис для тексту "${deleting?.originalText.substring(0, 60)}…" буде видалено. Наступний ідентичний запит знову піде до Gemini.`}
        onConfirm={handleDelete}
        onClose={() => setDeleting(null)}
      />
    </Box>
  );
}

// ---------- Page ----------

const TABS = [
  { value: 'food',       label: 'Їжа',          icon: <RestaurantIcon /> },
  { value: 'workout',    label: 'Тренування',    icon: <FitnessCenterIcon /> },
  { value: 'medication', label: 'Медикаменти',   icon: <MedicationIcon /> },
];

const AdminAiExtractCachePageContent: React.FC = () => {
  const [searchParams] = useSearchParams();
  const rawTab = searchParams.get('tab') ?? 'food';
  const activeTab = TABS.some(t => t.value === rawTab) ? rawTab : 'food';
  const config = TABS_CONFIG[activeTab];

  return (
    <Box data-i-doc={PageIndices.ADMIN_AI_EXTRACT}>
      <PageHint iDoc={config.tabIndex as any} />

      <AdaptiveTabs
        tabs={TABS}
        searchParamKey="tab"
        defaultValue="food"
      />

      <Box sx={{ mt: 2 }}>
        <ExtractCacheTab key={activeTab} config={config} />
      </Box>
    </Box>
  );
};

export const AdminAiExtractCachePage: React.FC = () => (
  <DashboardLayout>
    <AdminAiExtractCachePageContent />
  </DashboardLayout>
);
